import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { auth } from '@/shared/api/graphqlClient';
import { useLanguage, LanguageProvider } from '@/shared/components/shared/LanguageContext';

// Role-based login pages
const LOGIN_PAGES = {
  ADMIN: '/AdminLogin',
  STAFF_ADMIN: '/AdminLogin',
  ENGINE: '/EngineLogin',
  STAFF_ENGINE: '/EngineLogin',
  RESELLER: '/ResellerLogin',
  STAFF_RESELLER: '/ResellerLogin',
  MERCHANT: '/MerchantLogin',
  STAFF_MERCHANT: '/MerchantLogin',
};

// Role-based dashboard pages
const DASHBOARD_PAGES = {
  ADMIN: '/AdminDashboard',
  STAFF_ADMIN: '/AdminDashboard',
  ENGINE: '/EngineDashboard',
  STAFF_ENGINE: '/EngineDashboard',
  RESELLER: '/ResellerDashboard',
  STAFF_RESELLER: '/ResellerDashboard',
  MERCHANT: '/MerchantDashboard',
  STAFF_MERCHANT: '/MerchantDashboard',
};

/**
 * ProtectedRoute component that handles authentication and role-based access control
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authorized
 * @param {string[]} props.allowedRoles - Array of roles that can access this route (e.g., ['ADMIN', 'ENGINE'])
 * @param {boolean} props.requireAuth - Whether authentication is required (default: true)
 */
function ProtectedRouteContent({ children, allowedRoles = [], requireAuth = true }) {
  const { t } = useLanguage();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    userRole: null,
  });
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7245/ingest/ef176638-3b9d-4c9e-82cd-8fea599372f1', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'ProtectedRoute.jsx:62', message: 'useEffect triggered', data: { pathname: location.pathname, hasCheckedAuth: hasCheckedAuth.current }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'post-fix', hypothesisId: 'B' }) }).catch(() => { });
    // #endregion

    // Only check auth once per component mount, not on every pathname change
    if (hasCheckedAuth.current) {
      // #region agent log
      fetch('http://127.0.0.1:7245/ingest/ef176638-3b9d-4c9e-82cd-8fea599372f1', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'ProtectedRoute.jsx:66', message: 'auth already checked, skipping', data: {}, timestamp: Date.now(), sessionId: 'debug-session', runId: 'post-fix', hypothesisId: 'B' }) }).catch(() => { });
      // #endregion
      return;
    }

    setIsChecking(true);
    let isMounted = true;
    hasCheckedAuth.current = true;

    const checkAuthWithDelay = async () => {
      // #region agent log
      // #endregion
      // Minimum 1 second delay promise
      const minDelay = new Promise(resolve => setTimeout(resolve, 1000));

      // Auth check promise
      const authCheck = new Promise(resolve => {
        const isAuthenticated = auth.isAuthenticated();
        const userRole = auth.getUserRole();
        // #region agent log
        // #endregion

        resolve({
          isAuthenticated,
          userRole: userRole?.toUpperCase() || null,
        });
      });

      // Wait for BOTH to complete
      const [authResult] = await Promise.all([authCheck, minDelay]);
      // #region agent log
      // #endregion

      if (isMounted) {
        setAuthState(authResult);
        setIsChecking(false);
        // #region agent log
        // #endregion
      } else {
        // #region agent log
        // #endregion
      }
    };

    checkAuthWithDelay();

    return () => {
      isMounted = false;
      // #region agent log
      // #endregion
    };
  }, []); // Empty dependency array - only run once on mount


  // Show nothing while checking auth
  if (isChecking) {
    // #region agent log
    // #endregion
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">{t("loading")}</p>
        </div>
      </div>
    );
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !authState.isAuthenticated) {
    // Redirect to root login page
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // If user is authenticated and trying to access a login page, redirect to their dashboard
  if (authState.isAuthenticated) {
    const loginPages = Object.values(LOGIN_PAGES);
    const isLoginPage = loginPages.includes(location.pathname) || location.pathname === '/';

    if (isLoginPage) {
      // Redirect authenticated user to their appropriate dashboard
      const userDashboard = DASHBOARD_PAGES[authState.userRole] || DASHBOARD_PAGES.ADMIN;
      return <Navigate to={userDashboard} replace />;
    }
  }

  // If role-based access is required, check if user has the required role
  if (allowedRoles.length > 0 && !allowedRoles.includes(authState.userRole)) {
    // #region agent log
    // #endregion
    // User is authenticated but doesn't have the required role
    // Redirect to their appropriate dashboard
    const userDashboard = DASHBOARD_PAGES[authState.userRole] || LOGIN_PAGES.ADMIN;
    return <Navigate to={userDashboard} replace />;
  }

  // User is authenticated and has the required role (or no role check is needed)
  return <>{children}</>;
}

export default function ProtectedRoute(props) {
  return (
    <ProtectedRouteContent {...props} />
  );
}





